/* eslint-disable react/no-unescaped-entities */
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ArrowUpRight, Hammer, HardHat } from "lucide-react";
import { SEO, faqSchema } from "@/components/SEO";
import { Section, SectionHeading, SpecLabel } from "@/components/Section";
import { BookACallButton } from "@/components/BookACallButton";
import { SystemCard } from "@/components/SystemCard";
import { SystemIcon } from "@/components/SystemIcon";
import { Reveal } from "@/components/Reveal";
import { ReviewBadges } from "@/components/ReviewBadges";
import { WhoWeWorkWith } from "@/components/WhoWeWorkWith";
import { PartnerStrip } from "@/components/PartnerStrip";
import { SYSTEMS } from "@/data/systems";
import { FAQS } from "@/data/faqs";
import { PORTFOLIO } from "@/data/portfolio";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";

export default function HomePage() {
    // Memoize the schemas array so the SEO component sees a stable reference
    // across renders (prevents react-helmet-async from re-running diff).
    const seoSchemas = useMemo(() => [faqSchema(FAQS)], []);

    return (
        <>
            <SEO
                title="Marketing Systems for Plumbers, HVAC, Roofers & Home Service Pros"
                description="Elevara Systems installs the 7 marketing systems home service businesses actually need — website, reviews, missed-call text-back, campaigns, local SEO, unified inbox, and business phone. One plan, $297/mo. No contracts. No bull*."
                path="/"
                schemas={seoSchemas}
            />

            {/* =================== HERO =================== */}
            <section
                className="relative overflow-hidden bg-white"
                data-testid="home-hero"
            >
                <div
                    className="absolute inset-0 blueprint-grid opacity-40 pointer-events-none"
                    aria-hidden="true"
                />
                <div
                    className="absolute inset-0 hero-edge-glow pointer-events-none"
                    aria-hidden="true"
                />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-12 sm:pt-16 sm:pb-20 lg:pt-24 lg:pb-28">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                        {/* Copy */}
                        <div className="lg:col-span-7 order-2 lg:order-1">
                            <div className="inline-flex items-center gap-2 rounded-full bg-[#188bf6]/8 border border-[#188bf6]/25 px-3 py-1.5">
                                <HardHat
                                    className="w-3.5 h-3.5 text-[#188bf6]"
                                    aria-hidden="true"
                                />
                                <span className="text-[10px] sm:text-[11px] font-display font-bold uppercase tracking-[0.18em] text-[#188bf6]">
                                    Built for home service operators
                                </span>
                            </div>
                            <h1 className="mt-4 font-display font-extrabold tracking-tight text-[#0a0b0d] text-[34px] leading-[1.05] sm:text-5xl lg:text-[64px] lg:leading-[1.02]">
                                Marketing systems
                                <br />
                                <span>that actually </span>
                                <span className="text-[#188bf6]">work.</span>
                            </h1>
                            <p className="mt-5 text-[15px] sm:text-base lg:text-lg text-[#4a5562] max-w-2xl leading-relaxed">
                                One plan. Seven systems. Built for plumbers,
                                roofers, HVAC pros, electricians, and every
                                home service operator tired of being sold
                                bull* by marketing agencies. No contracts,
                                no setup fees, no hype.
                            </p>
                            <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                                <BookACallButton
                                    size="lg"
                                    testId="home-hero-book-a-call-button"
                                />
                                <Link
                                    to="/pricing"
                                    data-testid="home-hero-pricing-link"
                                    className="inline-flex items-center justify-center gap-2 h-12 sm:h-14 lg:h-16 px-5 text-sm sm:text-base font-display font-bold text-[#0a0b0d] border-2 border-[#0a0b0d]/15 hover:border-[#188bf6] hover:text-[#188bf6] rounded-xl transition-colors"
                                >
                                    See pricing
                                    <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>

                            {/* Review badges */}
                            <div className="mt-7 sm:mt-9">
                                <p className="text-[10px] sm:text-[11px] font-display font-bold uppercase tracking-[0.18em] text-[#4a5562] mb-3">
                                    Trusted across the platforms that matter
                                </p>
                                <ReviewBadges />
                            </div>
                        </div>

                        {/* Mascot — hidden on mobile */}
                        <div className="lg:col-span-5 order-1 lg:order-2 relative hidden lg:block">
                            <div className="relative mx-auto max-w-xl lg:max-w-none">
                                <div
                                    className="absolute -inset-10 sm:-inset-16 rounded-full blur-3xl"
                                    style={{
                                        background:
                                            "radial-gradient(closest-side, rgba(24,139,246,0.22), transparent 70%)",
                                    }}
                                    aria-hidden="true"
                                />
                                <img
                                    src="/assets/mascot_pose5.png"
                                    alt="Elevara Systems mascot"
                                    className="relative w-full max-w-[560px] mx-auto h-auto select-none drop-shadow-[0_30px_40px_rgba(10,11,13,0.18)]"
                                    draggable="false"
                                    loading="eager"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* =================== WHAT WE DO (compact) =================== */}
            <Section tone="surface" testId="home-what-we-do" className="section-depth-top">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
                    <div className="lg:col-span-5">
                        <SpecLabel>What we do</SpecLabel>
                        <h2 className="mt-3 sm:mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0a0b0d]">
                            Marketing built for trucks, not boardrooms.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 space-y-3.5 text-[#4a5562] text-base sm:text-lg leading-relaxed">
                        <p>
                            Elevara Systems is what happens when a marketing
                            company actually understands how a home service
                            business runs.
                        </p>
                        <p>
                            We don't sell campaigns, retainers, or fluffy
                            “growth strategies.” We install seven concrete
                            systems that turn missed calls into booked
                            jobs, customers into 5-star reviews, and your
                            website into the hardest-working employee on
                            the team.
                        </p>
                        <p className="font-display font-bold text-[#0a0b0d]">
                            It runs in the background. You run the business.
                        </p>
                    </div>
                </div>
            </Section>

            {/* =================== WHO WE WORK WITH =================== */}
            <WhoWeWorkWith />

            {/* =================== PARTNERS / INTEGRATIONS =================== */}
            <PartnerStrip tone="dark" />

            {/* =================== THE STACK =================== */}
            <Section testId="home-systems" tone="surface">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 sm:gap-6 mb-8 sm:mb-10">
                    <div>
                        <SpecLabel>The Stack</SpecLabel>
                        <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0a0b0d]">
                            Seven systems.
                            <br />
                            <span className="text-[#188bf6]">One plan.</span>
                        </h2>
                    </div>
                    <p className="text-sm sm:text-base text-[#4a5562] max-w-md leading-relaxed">
                        Each system on its own solves a real problem. Together
                        they compound. You get all seven on day one.
                    </p>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    {SYSTEMS.map((sys, i) => (
                        <Reveal key={sys.slug} delay={i * 0.03}>
                            <SystemCard
                                system={sys}
                                compact
                                className="h-full"
                            />
                        </Reveal>
                    ))}
                    {/* 8th cell = CTA tile */}
                    <Reveal delay={0.24}>
                        <a
                            href="https://grow.theelevara.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="home-stack-cta-tile"
                            className="group h-full rounded-2xl border-2 border-dashed border-[#188bf6]/40 bg-[#188bf6]/8 p-4 sm:p-5 flex flex-col justify-between hover:border-[#188bf6] hover:bg-[#188bf6] hover:text-white transition-all"
                        >
                            <div>
                                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#188bf6] text-white flex items-center justify-center">
                                    <Hammer
                                        className="w-4 h-4 sm:w-5 sm:h-5"
                                        aria-hidden="true"
                                    />
                                </div>
                                <div className="mt-4 sm:mt-5 text-[10px] font-display font-bold uppercase tracking-[0.18em] text-[#0a0b0d]/60 group-hover:text-white/80">
                                    Get the stack
                                </div>
                                <h3 className="mt-1 text-base sm:text-lg lg:text-xl font-display font-extrabold tracking-tight text-[#0a0b0d] group-hover:text-white">
                                    Book a 15-min call.
                                </h3>
                            </div>
                            <div className="mt-3 sm:mt-4 inline-flex items-center gap-1.5 text-xs sm:text-sm font-display font-bold text-[#188bf6] group-hover:text-white">
                                Start now
                                <ArrowUpRight
                                    className="w-3.5 h-3.5 sm:w-4 sm:h-4"
                                    aria-hidden="true"
                                />
                            </div>
                        </a>
                    </Reveal>
                </div>
            </Section>

            {/* =================== PRICING TEASER =================== */}
            <Section testId="home-pricing-teaser">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
                    <div className="lg:col-span-7">
                        <SpecLabel>Pricing</SpecLabel>
                        <h2 className="mt-3 sm:mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0a0b0d]">
                            One plan. $297 a month.
                            <br />
                            <span className="text-[#188bf6]">All seven systems.</span>
                        </h2>
                        <p className="mt-4 sm:mt-5 text-sm sm:text-base lg:text-lg text-[#4a5562] max-w-xl leading-relaxed">
                            No setup fee. No contract. Cancel anytime.
                        </p>
                        <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3">
                            <BookACallButton
                                size="md"
                                testId="home-pricing-book-a-call-button"
                            />
                            <Link
                                to="/pricing"
                                data-testid="home-pricing-see-link"
                                className="inline-flex items-center justify-center gap-2 h-11 sm:h-12 px-5 text-sm font-display font-bold text-[#0a0b0d] border-2 border-[#0a0b0d]/15 hover:border-[#188bf6] hover:text-[#188bf6] rounded-xl transition-colors"
                            >
                                See pricing details
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>

                    {/* Spec card */}
                    <div className="lg:col-span-5">
                        <div className="relative rounded-2xl border-2 border-[#0a0b0d]/14 bg-white p-5 sm:p-7 lg:p-8 card-lift">
                            <span
                                aria-hidden="true"
                                className="absolute inset-x-6 top-0 h-1 bg-[#188bf6] rounded-b-full"
                            />
                            <div className="flex items-baseline justify-between">
                                <span className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-[#4a5562]">
                                    Foundational Advanced
                                </span>
                                <span className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-[#188bf6]">
                                    Active plan
                                </span>
                            </div>
                            <div className="mt-3 flex items-baseline gap-2">
                                <span className="font-display text-5xl sm:text-6xl font-extrabold tracking-tight text-[#0a0b0d]">
                                    $297
                                </span>
                                <span className="text-[#4a5562] text-sm font-display font-medium">
                                    / month
                                </span>
                            </div>
                            <p className="mt-1 text-sm text-[#4a5562]">
                                Everything. All seven systems. Always.
                            </p>
                            <div className="mt-5 h-px w-full bg-[#0a0b0d]/12" />
                            <ul className="mt-5 grid grid-cols-1 gap-2">
                                {SYSTEMS.map((s) => (
                                    <li
                                        key={s.slug}
                                        className="flex items-center gap-3 text-[13px] sm:text-sm font-display font-medium text-[#0a0b0d]"
                                    >
                                        <span className="w-7 h-7 rounded-md bg-[#188bf6]/10 border border-[#188bf6]/20 flex items-center justify-center shrink-0">
                                            <SystemIcon
                                                slug={s.slug}
                                                className="w-3.5 h-3.5 text-[#188bf6]"
                                            />
                                        </span>
                                        <span>{s.name}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </Section>

            {/* =================== OUR WORK =================== */}
            <Section testId="home-our-work" tone="surface">
                <SectionHeading
                    label="Our Work"
                    title="Real businesses. Real systems running."
                    description="Three home service operators currently running the Elevara stack."
                />
                <div className="mt-8 sm:mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                    {PORTFOLIO.map((p, i) => (
                        <Reveal key={p.title} delay={i * 0.05}>
                            <article
                                data-testid={`portfolio-card-${i + 1}`}
                                className="group rounded-2xl overflow-hidden border-2 border-[#0a0b0d]/14 bg-white hover:border-[#188bf6] hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(10,11,13,0.18)] transition-all card-lift"
                            >
                                <div className="aspect-[16/10] overflow-hidden bg-[#F7F8FA] border-b border-[#0a0b0d]/12">
                                    <img
                                        src={p.image}
                                        alt={`${p.title} — ${p.accent} site by Elevara Systems.`}
                                        loading="lazy"
                                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.02]"
                                    />
                                </div>
                                <div className="p-4 sm:p-5">
                                    <div className="flex items-center justify-between">
                                        <span className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-[#4a5562]">
                                            {p.accent}
                                        </span>
                                        <ArrowUpRight
                                            className="w-4 h-4 text-[#0a0b0d]/30 group-hover:text-[#188bf6] transition-colors"
                                            aria-hidden="true"
                                        />
                                    </div>
                                    <h3 className="mt-2 font-display text-base sm:text-lg font-extrabold text-[#0a0b0d]">
                                        {p.title}
                                    </h3>
                                    <p className="mt-1 text-xs sm:text-sm text-[#4a5562]">
                                        {p.category}
                                    </p>
                                </div>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </Section>

            {/* =================== FAQ =================== */}
            <Section testId="home-faq">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
                    <div className="lg:col-span-4">
                        <SpecLabel>FAQ</SpecLabel>
                        <h2 className="mt-3 sm:mt-4 font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-[#0a0b0d]">
                            Straight answers to the
                            <br />
                            stuff you'd actually ask.
                        </h2>
                        <p className="mt-4 text-sm sm:text-base text-[#4a5562] max-w-md">
                            Still want a real human? Book a call — we
                            don't send you to a chatbot.
                        </p>
                        <div className="mt-5 sm:mt-6">
                            <BookACallButton
                                size="md"
                                testId="home-faq-book-a-call-button"
                            />
                        </div>
                    </div>
                    <div className="lg:col-span-8">
                        <Accordion
                            type="single"
                            collapsible
                            className="w-full"
                            data-testid="home-faq-accordion"
                        >
                            {FAQS.map((item, idx) => (
                                <AccordionItem
                                    key={item.q}
                                    value={`item-${idx}`}
                                    data-testid={`faq-item-${idx}`}
                                    className="border-b border-[#0a0b0d]/14"
                                >
                                    <AccordionTrigger
                                        data-testid={`faq-item-trigger-${idx}`}
                                        className="py-4 sm:py-5 text-left text-[15px] sm:text-base lg:text-lg font-display font-bold text-[#0a0b0d] hover:no-underline hover:text-[#188bf6]"
                                    >
                                        {item.q}
                                    </AccordionTrigger>
                                    <AccordionContent className="pb-5 text-sm sm:text-[15px] text-[#4a5562] leading-relaxed">
                                        {item.a}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </div>
            </Section>

            {/* =================== FINAL CTA — DARK BAND, SEPARATED FROM FOOTER =================== */}
            <section
                className="relative overflow-hidden bg-[#0a0b0d] text-white"
                data-testid="home-final-cta"
            >
                <div className="h-1 caution-stripe" aria-hidden="true" />
                <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(700px circle at 80% 50%, rgba(24,139,246,0.18), transparent 60%)",
                    }}
                    aria-hidden="true"
                />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                        <div className="lg:col-span-8">
                            <SpecLabel tone="dark">Ready to install the systems?</SpecLabel>
                            <h2 className="mt-3 sm:mt-4 font-display font-extrabold text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
                                Stop bleeding leads.
                                <br />
                                <span className="text-[#5BA9F8]">Book a call.</span>
                            </h2>
                            <p className="mt-4 sm:mt-5 text-sm sm:text-base lg:text-lg text-white/72 max-w-xl leading-relaxed">
                                15 minutes. No sales-y nonsense. We tell you
                                straight if the systems are a fit — and if
                                they're not, we say that too.
                            </p>
                            <div className="mt-6 sm:mt-8">
                                <BookACallButton
                                    size="lg"
                                    testId="home-final-cta-button"
                                />
                            </div>
                        </div>
                        <div className="lg:col-span-4 hidden lg:block">
                            <img
                                src="/assets/mascot_pose1.png"
                                alt=""
                                className="w-full max-w-[320px] mx-auto h-auto"
                                loading="lazy"
                                aria-hidden="true"
                            />
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
